import {
  AlertCircle,
  CheckCircle2,
  CloudUpload,
  File as FileIcon,
  FileStack,
  FileUp,
  Link as LinkIcon,
  Monitor,
  Upload
} from "lucide-react";
import { DragEvent, ReactElement, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { BrandMark } from "@/components/brand";
import { GoogleDriveIcon, OneDriveIcon } from "@/components/icons/brands";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { IconTile } from "@/components/ui/icon-tile";
import { Progress } from "@/components/ui/progress";
import { Tag } from "@/components/ui/tag";
import { cn } from "@/lib/utils";
import { supportedExtensionsString } from "../constans";
// eslint-disable-next-line import/no-cycle
import { useGoogleDrive } from "../hooks/useGoogleDrive";
import { useSharePoint } from "../hooks/useSharePoint";
import { setIsLoading } from "../redux/file-upload/fileUploadSlice";
import { uploadFileSelectors } from "../redux/file-upload/selectors";
import { UploadUrlDocumentModal } from "./Documents/UploadUrlDocumentModal";

const truncateFileName = (fileName: string, maxLength = 20) => {
  if (fileName.length <= maxLength) return fileName;

  const extension = fileName.includes(".") ? fileName.split(".").pop() : "";
  const nameWithoutExt = fileName.includes(".")
    ? fileName.substring(0, fileName.lastIndexOf("."))
    : fileName;

  const truncatedName = `${nameWithoutExt.substring(0, maxLength)}...${
    extension ? `.${extension}` : ""
  }`;
  return truncatedName;
};

const SHAREPOINT_THESVG_LOGO_URL =
  "https://cdn.jsdelivr.net/gh/glincker/thesvg@main/public/icons/microsoft-sharepoint/default.svg";

const PROCESSING_LINE_WIDTHS = ["w-18", "w-23", "w-23", "w-18", "w-23", "w-23"];

export enum UploaderLayout {
  Vertical = "vertical",
  Horizontal = "horizontal"
}

type UploaderToastIcon = "FeatherCheck" | "FeatherAlertCircle";

type UploaderToast = {
  variant: "brand" | "neutral" | "error" | "success";
  icon?: UploaderToastIcon;
  title: string;
  description?: string;
  durationMs?: number;
};

interface UploaderProps {
  onUpload: (files: FileList | null) => void;
  layout?: UploaderLayout;
  dragAndDropCta?: string;
  dragAndDropIcon?: ReactElement;
  buttonCta?: string;
  buttonIcon?: ReactElement;
  /** Ignored: the uploader no longer renders an illustration. */
  withBannerImg?: boolean;
  isDisabled?: boolean;
  storagePath?: string | null;
  collectionPath?: string | null;
  showFileName?: boolean | null;
  maxExternalLinks?: number | null;
  onUploadCompositePdf?: () => void;
  isUploadCompositeDisabled?: boolean;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  variant?: "default" | "canonical";
  isProcessing?: boolean;
  showToast?: (toast: UploaderToast) => void;
  showUploadOptionsAsTiles?: boolean;
  hideUrlUploadOption?: boolean;
  showUploadProgressTiles?: boolean;
  uploadSurfaceVariant?: "default" | "largeCard";
  maxFiles?: number | null;
}

type UploadOptionTile = {
  key: string;
  label: string;
  icon: ReactElement;
  onClick: () => void;
  isDisabled: boolean;
  badge?: string;
};

export const Uploader = ({
  onUpload,
  layout = UploaderLayout.Horizontal,
  dragAndDropCta = "Drag and drop here",
  dragAndDropIcon = <CloudUpload className="size-4 text-accent-seed" />,
  buttonCta = "Browse Files",
  buttonIcon,
  withBannerImg = false,
  isDisabled = false,
  collectionPath,
  storagePath,
  showFileName = false,
  maxExternalLinks = null,
  onUploadCompositePdf,
  isUploadCompositeDisabled,
  emptyStateTitle = "Upload Evidence",
  emptyStateDescription = "Add evidence to your case by dragging and dropping files here, or choosing an upload option below.",
  variant = "default",
  isProcessing = false,
  showToast,
  showUploadOptionsAsTiles = false,
  hideUrlUploadOption = false,
  showUploadProgressTiles = true,
  uploadSurfaceVariant = "default",
  maxFiles = null
}: UploaderProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const minimumProcessingTimeoutRef = useRef<number | null>(null);
  const isProcessingRef = useRef(isProcessing);
  const dispatch = useDispatch();

  const [isUrlModalOpen, setIsUrlModalOpen] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [numUploadedFiles, setNumUploadedFiles] = useState(0);
  const [isDragActive, setIsDragActive] = useState(false);
  const [shouldShowProcessing, setShouldShowProcessing] = useState(false);
  const [isUploadMenuOpen, setIsUploadMenuOpen] = useState(false);
  const isShowingProcessing = isProcessing || shouldShowProcessing;
  const isUrlUploadEnabled =
    import.meta.env.REACT_APP_ENABLE_UPLOAD_FROM_URL === "true";
  const {
    files: uploadFilesState,
    uploadProgress,
    fileErrors
  } = useSelector(uploadFileSelectors.selectAll);

  const showUploaderToast = ({
    title,
    variant: toastVariant,
    icon,
    description,
    durationMs = 3000
  }: UploaderToast) => {
    if (showToast) {
      showToast({
        title,
        variant: toastVariant,
        icon,
        description,
        durationMs
      });
      return;
    }

    const notify = toastVariant === "error" ? toast.error : toast.success;
    notify(title, { description, duration: durationMs });
  };

  const closeUploaderToasts = () => {
    if (!showToast) {
      toast.dismiss();
    }
  };

  const { pickFiles: pickSharePointFiles } = useSharePoint();
  const { pickFiles: pickGoogleDriveFiles, isLoaded: isGoogleDriveLoaded } =
    useGoogleDrive();

  useEffect(() => {
    isProcessingRef.current = isProcessing;

    if (isProcessing) {
      setShouldShowProcessing(true);

      if (minimumProcessingTimeoutRef.current) {
        window.clearTimeout(minimumProcessingTimeoutRef.current);
      }

      minimumProcessingTimeoutRef.current = window.setTimeout(() => {
        minimumProcessingTimeoutRef.current = null;

        if (!isProcessingRef.current) {
          setShouldShowProcessing(false);
        }
      }, 3000);

      return;
    }

    if (!minimumProcessingTimeoutRef.current) {
      setShouldShowProcessing(false);
    }
  }, [isProcessing]);

  useEffect(
    () => () => {
      if (minimumProcessingTimeoutRef.current) {
        window.clearTimeout(minimumProcessingTimeoutRef.current);
      }
    },
    []
  );

  const handleClick = () => {
    if (isDisabled || isShowingProcessing) return;
    fileInputRef?.current?.click();
  };

  const openUploadMenu = () => {
    if (isDisabled || isShowingProcessing) return;
    setIsUploadMenuOpen(true);
  };

  const filesToFileList = (files: File[]): FileList => {
    try {
      const dt = new DataTransfer();
      files.forEach((f) => dt.items.add(f));
      return dt.files;
    } catch {
      const fileList = {
        length: files.length,
        item: (index: number) => files[index],
        *[Symbol.iterator]() {
          yield* files;
        },
        ...files.reduce((acc, file, index) => ({ ...acc, [index]: file }), {})
      } as FileList;
      return fileList;
    }
  };

  const normalizeFilesForUpload = (files: FileList | File[]): FileList => {
    const fileArray = Array.from(files);
    const limitedFiles =
      typeof maxFiles === "number" && maxFiles > 0
        ? fileArray.slice(0, maxFiles)
        : fileArray;

    return filesToFileList(limitedFiles);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = () => {
    setIsDragActive(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    const { files } = e.dataTransfer;
    if (files && files.length > 0) {
      const uploadFiles = normalizeFilesForUpload(files);
      setUploadedFileName(uploadFiles[0].name);
      setNumUploadedFiles(uploadFiles.length);
      onUpload(uploadFiles);
      showUploaderToast({
        title: "Files uploaded successfully.",
        variant: "success",
        icon: "FeatherCheck",
        durationMs: 3000
      });
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files && files.length > 0) {
      const uploadFiles = normalizeFilesForUpload(files);
      setUploadedFileName(uploadFiles[0].name);
      setNumUploadedFiles(uploadFiles.length);
      onUpload(uploadFiles);
    }
    // allow re-selecting same file(s)
    e.target.value = "";
  };

  useEffect(() => {
    // Cleanup function to clear files on unmount
    return () => {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };
  }, []);

  const handleSharePointSelect = async () => {
    try {
      dispatch(setIsLoading(true));

      // Get files from SharePoint as regular File objects
      const files = await pickSharePointFiles();

      if (files.length > 0) {
        const fileList = normalizeFilesForUpload(files);

        // Use the standard onUpload handler
        setUploadedFileName(fileList[0].name);
        setNumUploadedFiles(fileList.length);
        onUpload(fileList);
      }
    } catch (error) {
      closeUploaderToasts();
      showUploaderToast({
        title: "Error retrieving files from SharePoint",
        variant: "error",
        icon: "FeatherAlertCircle",
        durationMs: 3000
      });
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const handleGoogleDriveSelect = async () => {
    try {
      dispatch(setIsLoading(true));

      const files = await pickGoogleDriveFiles();

      if (files.length > 0) {
        const fileList = normalizeFilesForUpload(files);

        setUploadedFileName(fileList[0].name);
        setNumUploadedFiles(fileList.length);
        onUpload(fileList);
      }
    } catch (error) {
      closeUploaderToasts();
      showUploaderToast({
        title: "Error retrieving files from Google Drive",
        variant: "error",
        icon: "FeatherAlertCircle",
        durationMs: 3000
      });
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const showsSingleFileName = Boolean(showFileName) && numUploadedFiles === 1;

  const renderUploadButton = () => (
    <DropdownMenu open={isUploadMenuOpen} onOpenChange={setIsUploadMenuOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="primary"
          size={layout === UploaderLayout.Horizontal ? "sm" : "md"}
        >
          {showsSingleFileName ? <CheckCircle2 /> : buttonIcon}
          {showsSingleFileName && uploadedFileName
            ? truncateFileName(uploadedFileName)
            : buttonCta}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-auto min-w-56"
        onClick={(e) => e.stopPropagation()}
      >
        <DropdownMenuItem onSelect={handleClick}>
          <Monitor />
          Upload From Computer
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={handleGoogleDriveSelect}
          disabled={!isGoogleDriveLoaded}
        >
          <GoogleDriveIcon />
          Choose From Google Drive
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={handleSharePointSelect}>
          <OneDriveIcon />
          Choose From SharePoint
        </DropdownMenuItem>
        {isUrlUploadEnabled && (
          <DropdownMenuItem
            onSelect={() => setIsUrlModalOpen(true)}
            disabled={isDisabled}
          >
            <LinkIcon />
            Upload From URL
          </DropdownMenuItem>
        )}
        {onUploadCompositePdf && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => {
                if (!isUploadCompositeDisabled) {
                  onUploadCompositePdf();
                }
              }}
              disabled={isUploadCompositeDisabled}
            >
              <FileStack />
              Upload Multi-Document PDF
              <Tag>BETA</Tag>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const renderDragAndDrop = () => (
    <div className="flex items-center gap-2">
      {dragAndDropIcon}
      <span className="font-medium text-accent-seed">{dragAndDropCta}</span>
    </div>
  );

  const uploadOptionTiles: UploadOptionTile[] = [
    {
      key: "computer",
      label: "Computer",
      icon: <Monitor className="size-5" />,
      onClick: handleClick,
      isDisabled
    },
    {
      key: "google-drive",
      label: "Google Drive",
      icon: <GoogleDriveIcon className="size-7" />,
      onClick: handleGoogleDriveSelect,
      isDisabled: isDisabled || !isGoogleDriveLoaded
    },
    {
      key: "sharepoint",
      label: "SharePoint",
      icon: (
        <img
          src={SHAREPOINT_THESVG_LOGO_URL}
          alt=""
          className="size-7 object-contain"
        />
      ),
      onClick: handleSharePointSelect,
      isDisabled
    },
    ...(isUrlUploadEnabled && !hideUrlUploadOption
      ? [
          {
            key: "url",
            label: "URL",
            icon: <LinkIcon className="size-5" />,
            onClick: () => setIsUrlModalOpen(true),
            isDisabled
          }
        ]
      : []),
    ...(onUploadCompositePdf
      ? [
          {
            key: "multi-document-pdf",
            label: "Multi-Doc PDF",
            icon: <FileStack className="size-5" />,
            onClick: onUploadCompositePdf,
            isDisabled: Boolean(isUploadCompositeDisabled),
            badge: "BETA"
          }
        ]
      : [])
  ];

  const renderUploadTiles = () => (
    <div className="flex w-full flex-wrap justify-center gap-3">
      {showUploadProgressTiles && uploadFilesState.length > 0
        ? uploadFilesState.map((file) => {
            const progress = Math.round(uploadProgress[file.name] ?? 0);
            const hasError = Boolean(fileErrors[file.name]);
            const isComplete = !hasError && progress >= 100;

            return (
              <div
                key={`${file.name}-${file.lastModified}`}
                className={cn(
                  "flex size-28 flex-col items-center justify-center gap-2 rounded-xl px-3",
                  hasError
                    ? "bg-danger-soft text-danger"
                    : "bg-fill-2 text-fg-3"
                )}
              >
                {hasError ? (
                  <AlertCircle className="size-4" />
                ) : (
                  <FileIcon className="size-4" />
                )}
                <span
                  className={cn(
                    "line-clamp-2 w-full text-center text-sm font-medium",
                    hasError ? "text-danger" : "text-fg"
                  )}
                >
                  {file.name}
                </span>
                <Progress
                  value={hasError ? 100 : progress}
                  className={cn(
                    "bg-fill-3",
                    hasError && "*:data-[slot=progress-indicator]:bg-danger"
                  )}
                />
                <span
                  className={cn(
                    "text-xs",
                    hasError ? "text-danger" : "text-fg-3"
                  )}
                >
                  {hasError
                    ? "Failed"
                    : isComplete
                    ? "Uploaded"
                    : `${progress}%`}
                </span>
              </div>
            );
          })
        : uploadOptionTiles.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                if (option.isDisabled) return;
                option.onClick();
              }}
              aria-disabled={option.isDisabled}
              className={cn(
                "flex size-28 flex-col items-center justify-center gap-2 rounded-xl bg-fill-2 text-fg-2 transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                option.isDisabled
                  ? "cursor-not-allowed opacity-50"
                  : "cursor-pointer hover:bg-fill-3 hover:text-fg"
              )}
            >
              <span className="flex h-7 items-center justify-center">
                {option.icon}
              </span>
              <span className="text-center text-base font-medium text-fg">
                {option.label}
              </span>
              {option.badge ? <Tag>{option.badge}</Tag> : null}
            </button>
          ))}
    </div>
  );

  const renderLargeCardSourceButton = ({
    key,
    label,
    icon,
    onClick,
    isDisabled: isSourceDisabled = false
  }: {
    key: string;
    label: string;
    icon: ReactElement;
    onClick?: () => void;
    isDisabled?: boolean;
  }) => (
    <Button
      key={key}
      type="button"
      variant="secondary"
      size="md"
      onClick={(event) => {
        event.stopPropagation();
        if (isSourceDisabled || !onClick) return;
        onClick();
      }}
      aria-disabled={isSourceDisabled}
      className={cn(
        "w-full bg-elevated sm:w-auto sm:min-w-39",
        isSourceDisabled && "cursor-default hover:bg-elevated"
      )}
    >
      {icon}
      <span>{label}</span>
    </Button>
  );

  const renderLargeUploadCard = () => (
    <div className="flex min-h-85 items-center justify-center px-5 py-10 sm:min-h-80 sm:py-9 md:min-h-75 md:px-6 md:py-8">
      <div className="flex w-full flex-col items-center gap-4 text-center">
        <div className="flex flex-col items-center gap-6">
          <IconTile icon={FileUp} size="xl" className="bg-elevated text-fg-3" />
          <div className="flex flex-col items-center gap-1">
            <p className="text-md text-fg md:text-lg">{dragAndDropCta}</p>
            <p className="text-sm text-fg-3">
              PDF, DOCX, XLSX, CSV, PPTX, PNG, or JPG
            </p>
          </div>
          {/* PublicOutgoingIntake restyles this button through the `intake-upload-button` class. */}
          <Button
            type="button"
            variant="primary"
            size="md"
            className="intake-upload-button"
            onClick={(event) => {
              event.stopPropagation();
              handleClick();
            }}
            disabled={isDisabled || isShowingProcessing}
          >
            <Upload />
            {buttonCta}
          </Button>
        </div>

        <div className="flex w-full flex-col items-center gap-3">
          <p className="text-sm text-fg-4">Or choose from</p>
          <div className="flex w-full max-w-140 flex-wrap items-center justify-center gap-2">
            {[
              {
                key: "sharepoint",
                label: "SharePoint",
                icon: (
                  <img
                    src={SHAREPOINT_THESVG_LOGO_URL}
                    alt=""
                    className="size-5 object-contain"
                  />
                ),
                onClick: handleSharePointSelect,
                isDisabled
              },
              {
                key: "google-drive",
                label: "Google Drive",
                icon: <GoogleDriveIcon className="size-5" />,
                onClick: handleGoogleDriveSelect,
                isDisabled: isDisabled || !isGoogleDriveLoaded
              }
            ].map(renderLargeCardSourceButton)}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSupportedFormats = () => (
    <p
      className={cn(
        "text-sm text-fg-3",
        layout === UploaderLayout.Vertical ? "mt-3" : "mt-2"
      )}
    >
      Supported formats: {supportedExtensionsString}
    </p>
  );

  const renderProcessing = () => (
    <div className="flex h-81 w-full items-center justify-center rounded-lg bg-accent-soft">
      <div className="flex w-33 animate-pulse flex-col gap-2 rounded-md bg-elevated p-4 ring-1 ring-accent-line">
        <BrandMark title="CaseBlink" className="mb-1 h-7 w-5.5" />
        {PROCESSING_LINE_WIDTHS.map((width, index) => (
          <div
            key={index}
            className={cn("h-1 rounded-full bg-fill-3", width)}
          />
        ))}
      </div>
    </div>
  );

  const isCanonicalProcessing = variant === "canonical" && isShowingProcessing;
  const isLargeCard = uploadSurfaceVariant === "largeCard";

  return (
    <div className="flex flex-col text-start">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileInputChange}
        style={{ display: "none" }}
        className="hidden"
        disabled={isDisabled || isShowingProcessing}
        multiple={maxFiles !== 1}
        accept={supportedExtensionsString}
      />
      <div
        className={cn(
          "rounded-lg border text-center transition-colors duration-150",
          isShowingProcessing ? "cursor-default" : "cursor-pointer",
          isCanonicalProcessing && !isLargeCard
            ? "border-transparent"
            : isDragActive
            ? "border-dashed border-accent-line bg-accent-soft"
            : "border-dashed border-line-3 bg-fill-1 hover:bg-fill-2",
          isLargeCard || isCanonicalProcessing
            ? "p-0"
            : variant === "canonical"
            ? "p-8"
            : layout === UploaderLayout.Vertical
            ? "p-10"
            : "p-5"
        )}
        onClick={(e) => {
          if (isShowingProcessing) return;
          if (isLargeCard) {
            handleClick();
            return;
          }
          if (showUploadOptionsAsTiles) return;
          if (!(e.target as HTMLElement).closest("button")) {
            openUploadMenu();
          }
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isCanonicalProcessing ? (
          renderProcessing()
        ) : isLargeCard ? (
          renderLargeUploadCard()
        ) : variant === "canonical" && showUploadOptionsAsTiles ? (
          <div className="flex min-h-65 items-center justify-center px-4">
            <div className="flex w-full flex-col items-center gap-4 text-center">
              <div className="flex flex-col items-center gap-1">
                <p className="text-lg text-fg">
                  <span className="font-medium text-accent-seed">
                    Choose an upload source
                  </span>{" "}
                  or drag here
                </p>
                {renderSupportedFormats()}
              </div>
              {renderUploadTiles()}
            </div>
          </div>
        ) : variant === "canonical" ? (
          <div className="flex min-h-65 items-center justify-center">
            <div className="flex flex-col items-center gap-5 text-center">
              <IconTile
                icon={CloudUpload}
                size="xl"
                className="bg-accent-soft text-accent-seed"
              />
              <div className="flex flex-col items-center gap-2">
                <p className="text-lg text-fg">
                  <span className="font-medium text-accent-seed">
                    Choose file
                  </span>{" "}
                  or drag here
                </p>
                {renderSupportedFormats()}
              </div>
            </div>
          </div>
        ) : layout === UploaderLayout.Vertical ? (
          <div className="flex justify-center">
            <div className="flex max-w-130 flex-col items-center gap-8 text-center">
              <div className="pt-8 pb-2">
                <IconTile
                  icon={CloudUpload}
                  size="xl"
                  className="bg-accent-soft text-accent-seed"
                />
              </div>
              <div className="flex flex-col items-center gap-2">
                <h2 className="text-lg font-medium text-fg">
                  {emptyStateTitle}
                </h2>
                <p className="text-sm text-fg-3">{emptyStateDescription}</p>
              </div>
              <div className="flex flex-col items-center gap-3">
                {showUploadOptionsAsTiles ? (
                  renderUploadTiles()
                ) : (
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    {renderUploadButton()}
                  </div>
                )}
              </div>
              {renderSupportedFormats()}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-4">
              {renderDragAndDrop()}
              <div className="flex items-center gap-2">
                {renderUploadButton()}
              </div>
            </div>
            {renderSupportedFormats()}
          </div>
        )}
      </div>
      {isUrlUploadEnabled && (
        <UploadUrlDocumentModal
          isOpen={isUrlModalOpen}
          onClose={() => setIsUrlModalOpen(false)}
          onConfirmSubmit={(files) => {
            if (!files || files.length === 0) return;
            const uploadFiles = normalizeFilesForUpload(files);
            setUploadedFileName(uploadFiles[0].name);
            setNumUploadedFiles(uploadFiles.length);
            onUpload(uploadFiles);
            showUploaderToast({
              title: "Document from URL added for upload",
              variant: "success",
              icon: "FeatherCheck",
              durationMs: 3000
            });
          }}
        />
      )}
    </div>
  );
};

export default Uploader;
